import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Pencil, Save, X, Plus, Wand2, ExternalLink, Trash2, Sparkles, RotateCcw, Users2, Copy, Check, CreditCard, Globe, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SubpagesEditor } from "../components/SubpagesEditor";
import { TeamEditor } from "../components/TeamEditor";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_STATUS = {
  geplant: { label: "Geplant", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
  generierung: { label: "Generierung...", color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" },
  überprüfung: { label: "Überprüfung", color: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400" },
  geliefert: { label: "Geliefert", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
  fehler: { label: "Fehler", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
};

export default function CompanyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [projects, setProjects] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [projectForm, setProjectForm] = useState({ name: "", template_id: "", notes: "" });
  const [portalAccounts, setPortalAccounts] = useState([]);
  const [showPortalModal, setShowPortalModal] = useState(false);
  const [portalForm, setPortalForm] = useState({ email: "", contact_name: "", plan: "starter" });
  const [creatingPortal, setCreatingPortal] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);
  const [requestedSubpages, setRequestedSubpages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [industryProfiles, setIndustryProfiles] = useState([]);
  const [detectedProfile, setDetectedProfile] = useState(null);
  const [prefillApplied, setPrefillApplied] = useState(false);

  const load = async () => {
    try {
      const [cRes, pRes, tRes, bRes, portalRes] = await Promise.all([
        axios.get(`${API}/companies/${id}`, { withCredentials: true }),
        axios.get(`${API}/projects/company/${id}`, { withCredentials: true }),
        axios.get(`${API}/templates`, { withCredentials: true }),
        axios.get(`${API}/bookings/company/${id}`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/companies/${id}/portal-accounts`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setCompany(cRes.data);
      setForm(cRes.data);
      setProjects(pRes.data);
      setTemplates(tRes.data);
      setBookings(bRes.data);
      setPortalAccounts(portalRes.data || []);
    } catch (err) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    axios.get(`${API}/industry-profiles`, { withCredentials: true })
      .then(r => setIndustryProfiles(r.data || []))
      .catch(() => {});
  }, []);

  // Detect industry profile whenever the company's industry changes
  useEffect(() => {
    const industry = company?.industry || "";
    if (!industry) { setDetectedProfile(null); return; }
    axios.get(`${API}/industry-profiles/detect`, {
      params: { industry, company_id: company?.id || "" },
      withCredentials: true,
    })
      .then(r => setDetectedProfile(r.data?.detected ? r.data : null))
      .catch(() => setDetectedProfile(null));
  }, [company?.industry, company?.id]);

  // When opening the project modal: if profile available and form still empty → prefill template + subpages
  const openProjectModal = () => {
    if (detectedProfile) {
      const tplId = detectedProfile.recommended_template || "";
      const tplExists = tplId && templates.some(t => t.id === tplId);
      setProjectForm({
        name: `Website ${company.name}`,
        template_id: tplExists ? tplId : "",
        notes: "",
      });
      const subs = (detectedProfile.suggested_pages || []).map(sp => ({
        title: sp.title,
        brief: sp.brief || "",
        in_nav: sp.in_nav !== false,
      }));
      setRequestedSubpages(subs);
      setPrefillApplied(true);
    } else {
      setProjectForm({ name: "", template_id: "", notes: "" });
      setRequestedSubpages([]);
      setPrefillApplied(false);
    }
    setShowProjectModal(true);
  };

  const resetPrefill = () => {
    setProjectForm(f => ({ ...f, template_id: "", name: f.name }));
    setRequestedSubpages([]);
    setPrefillApplied(false);
  };

  // Detect in edit-form (live) so the hint also appears while editing the company
  const liveEditDetected = (() => {
    const q = (form.industry || "").toLowerCase().trim();
    if (!q) return null;
    return industryProfiles.find(p =>
      p.label.toLowerCase() === q ||
      p.key === q ||
      (p.keywords || []).some(kw => q.includes(kw))
    ) || null;
  })();

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await axios.put(`${API}/companies/${id}`, form, { withCredentials: true });
      setCompany(updated.data);
      setForm(updated.data);
      setEditing(false);
      toast.success("Gespeichert");
    } catch (err) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCompany = async () => {
    if (!window.confirm(`Firma "${company.name}" wirklich löschen?`)) return;
    await axios.delete(`${API}/companies/${id}`, { withCredentials: true });
    toast.success("Firma gelöscht");
    navigate("/firmen");
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!projectForm.template_id) { toast.error("Bitte Template wählen"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/projects`, { ...projectForm, company_id: id, requested_subpages: requestedSubpages }, { withCredentials: true });
      toast.success(requestedSubpages.length > 0 ? `Projekt angelegt – ${requestedSubpages.length} Unterseite(n) vorgemerkt` : "Projekt angelegt");
      setShowProjectModal(false);
      setProjectForm({ name: "", template_id: "", notes: "" });
      setRequestedSubpages([]);
      setPrefillApplied(false);
      setProjects(p => [res.data, ...p]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProject = async (pid) => {
    if (!window.confirm("Projekt löschen?")) return;
    await axios.delete(`${API}/projects/${pid}`, { withCredentials: true });
    setProjects(p => p.filter(x => x.id !== pid));
    toast.success("Projekt gelöscht");
  };

  const handleCreatePortalAccess = async (e) => {
    e.preventDefault();
    setCreatingPortal(true);
    try {
      const res = await axios.post(`${API}/companies/${id}/portal-access`, portalForm, { withCredentials: true });
      toast.success("Kunden-Portal-Zugang erstellt!");
      const planNames = { starter: "Starter", business: "Business", premium: "Premium" };
      setPortalAccounts(prev => [...prev, {
        id: res.data.id, email: portalForm.email, contact_name: portalForm.contact_name,
        plan_name: planNames[portalForm.plan], is_active: true, subscription_status: "pending",
        portal_url: res.data.portal_url, created_at: new Date().toISOString(),
      }]);
      setShowPortalModal(false);
      setPortalForm({ email: "", contact_name: "", plan: "starter" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Erstellen");
    } finally {
      setCreatingPortal(false);
    }
  };

  const handleDeactivatePortal = async (accountId) => {
    try {
      await axios.delete(`${API}/portal-accounts/${accountId}`, { withCredentials: true });
      setPortalAccounts(prev => prev.map(a => a.id === accountId ? { ...a, is_active: false } : a));
      toast.success("Portal-Zugang deaktiviert");
    } catch { toast.error("Fehler"); }
  };

  const handleCopyLink = async (url, accountId) => {
    await navigator.clipboard.writeText(url).catch(() => {});
    setCopiedToken(accountId);
    toast.success("Link kopiert!");
    setTimeout(() => setCopiedToken(null), 2000);
  };



  if (loading) return <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">Laden...</div>;
  if (!company) return <div className="text-center text-sm text-muted-foreground py-12">Firma nicht gefunden</div>;

  const field = (key, label, type = "text") => (
    <div className="space-y-1">
      <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</Label>
      {editing
        ? <Input type={type} value={form[key] || ""} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} className="rounded-sm" />
        : <p className="text-sm text-foreground py-2 border-b border-border">{company[key] || "—"}</p>
      }
    </div>
  );

  return (
    <div className="max-w-6xl space-y-5">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/firmen" className="p-1.5 rounded-sm hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft size={16} />
        </Link>
        <div className="flex-1">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-0.5">Firmendaten</p>
          <h1 className="font-heading text-2xl font-black tracking-tight">{company.name}</h1>
        </div>
        <div className="flex gap-2">
          {editing ? (
            <>
              <Button variant="outline" size="sm" onClick={() => { setEditing(false); setForm(company); }} className="rounded-sm gap-1.5"><X size={13} /> Abbrechen</Button>
              <Button size="sm" onClick={handleSave} disabled={saving} className="rounded-sm gap-1.5"><Save size={13} /> {saving ? "..." : "Speichern"}</Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => setEditing(true)} className="rounded-sm gap-1.5" data-testid="edit-company-btn"><Pencil size={13} /> Bearbeiten</Button>
              <Button variant="destructive" size="sm" onClick={handleDeleteCompany} className="rounded-sm gap-1.5"><Trash2 size={13} /> Löschen</Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Company Info */}
        <div className="lg:col-span-2 bg-card border border-border rounded-sm p-5 space-y-4">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground border-b border-border pb-2">Unternehmensdaten</p>
          <div className="grid grid-cols-2 gap-4">
            {field("name", "Firmenname")}
            <div className="space-y-1">
              <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Branche
                {editing && liveEditDetected && (
                  <span
                    className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-primary/10 text-primary text-[10px] font-semibold normal-case tracking-normal"
                    data-testid="industry-detected-hint-edit"
                  >
                    <Sparkles size={9} /> {liveEditDetected.label}
                  </span>
                )}
                {!editing && detectedProfile && (
                  <span
                    className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-primary/10 text-primary text-[10px] font-semibold normal-case tracking-normal"
                    data-testid="industry-detected-hint"
                  >
                    <Sparkles size={9} /> {detectedProfile.label} ✓
                  </span>
                )}
              </Label>
              {editing ? (
                <>
                  <Input
                    list="industry-options"
                    value={form.industry || ""}
                    onChange={e => setForm(f => ({ ...f, industry: e.target.value }))}
                    placeholder="z.B. Friseur, Physiotherapie…"
                    className="rounded-sm"
                    data-testid="edit-company-industry-input"
                  />
                  <datalist id="industry-options">
                    {industryProfiles.map(p => (
                      <option key={p.key} value={p.label} />
                    ))}
                  </datalist>
                </>
              ) : (
                <p className="text-sm text-foreground py-2 border-b border-border">{company.industry || "—"}</p>
              )}
            </div>
            {field("contact_name", "Ansprechpartner")}
            {field("contact_email", "E-Mail", "email")}
            {field("contact_phone", "Telefon", "tel")}
            {field("website", "Website", "url")}
            {field("address", "Adresse")}
            {field("city", "Stadt")}
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Status</Label>
            {editing ? (
              <Select value={form.status || "aktiv"} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="aktiv">Aktiv</SelectItem>
                  <SelectItem value="inaktiv">Inaktiv</SelectItem>
                  <SelectItem value="lead">Lead</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm text-foreground py-2 border-b border-border">{company.status}</p>
            )}
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Beschreibung</Label>
            {editing
              ? <Textarea value={form.description || ""} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="rounded-sm" rows={2} />
              : <p className="text-sm text-foreground py-2 border-b border-border">{company.description || "—"}</p>
            }
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Notizen</Label>
            {editing
              ? <Textarea value={form.notes || ""} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="rounded-sm" rows={2} />
              : <p className="text-sm text-foreground py-2 border-b border-border">{company.notes || "—"}</p>
            }
          </div>
          <div className="pt-3 border-t border-border">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles size={14} className="text-primary" />
              <p className="text-xs font-semibold uppercase tracking-widest text-foreground">Branding-DNA &amp; echte Firmenzahlen <span className="normal-case text-muted-foreground/70 text-[10px] font-normal tracking-normal">(bestimmt Typo, Farben &amp; Stats)</span></p>
            </div>
            <p className="text-xs text-muted-foreground mb-3 italic">Diese Werte nutzt die KI statt erfundener Stats und leiten die visuelle DNA ab (Heritage/Startup, Playful/Natural/Premium, Trust-Badges).</p>
            <div className="grid grid-cols-3 gap-4">
              {field("founding_year", "Gründungsjahr")}
              {field("employee_count", "Mitarbeiter")}
              {field("locations_count", "Standorte")}
            </div>
            <div className="mt-3 space-y-3">
              <div className="space-y-1">
                <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Spezialisierungen</Label>
                {editing
                  ? <Textarea value={form.specialties || ""} onChange={e => setForm(f => ({ ...f, specialties: e.target.value }))} className="rounded-sm text-sm" rows={2} placeholder="z.B. Balayage, Brautfrisuren, Herrenschnitt" data-testid="edit-specialties" />
                  : <p className="text-sm text-foreground py-2 border-b border-border">{company.specialties || "—"}</p>
                }
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Auszeichnungen / Besonderheiten</Label>
                {editing
                  ? <Textarea value={form.achievements || ""} onChange={e => setForm(f => ({ ...f, achievements: e.target.value }))} className="rounded-sm text-sm" rows={2} placeholder="z.B. Ausbildungsbetrieb, Wella Partner, 'Salon des Jahres 2024'" data-testid="edit-achievements" />
                  : <p className="text-sm text-foreground py-2 border-b border-border">{company.achievements || "—"}</p>
                }
              </div>
            </div>
            {/* DNA Live-Preview */}
            {(() => {
              const c = editing ? form : company;
              const fy = c.founding_year || "";
              const ec = c.employee_count || "";
              const ach = c.achievements || "";
              const spec = c.specialties || "";
              const badges = [];
              if (fy && /^\d+$/.test(fy)) {
                const y = parseInt(fy, 10); const now = new Date().getFullYear(); const age = now - y;
                if (y < 1995 || age >= 30) badges.push({ k: "heritage", label: `Heritage (seit ${fy} → Serif-Typo, warm)`, cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300" });
                else if (y > 2018) badges.push({ k: "startup", label: `Startup (${fy} → Geo-Sans, kühl)`, cls: "bg-blue-500/15 text-blue-700 dark:text-blue-300" });
              }
              const h = `${spec} ${c.description || ""} ${c.name || ""}`.toLowerCase();
              if (/kinder|famili|baby|kids|kita/.test(h)) badges.push({ k: "playful", label: "Playful (runde Formen, Pastell)", cls: "bg-pink-500/15 text-pink-700 dark:text-pink-300" });
              else if (/bio|öko|oeko|nachhaltig|vegan|grün|organic/.test(h)) badges.push({ k: "natural", label: "Natural (Sage-Grün, Serif)", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" });
              else if (/luxus|premium|exklusiv|edel|gehoben/.test(h)) badges.push({ k: "premium", label: "Premium (reduziert, Serif)", cls: "bg-neutral-500/15 text-neutral-700 dark:text-neutral-300" });
              if (ach && /ausgezeichnet|preis|award|meister|zertifik|gold|sieger/i.test(ach)) badges.push({ k: "trust", label: "Trust-Badges im Hero", cls: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300" });
              if (ec && parseInt(ec, 10) >= 5) badges.push({ k: "team", label: "Team-Prominence", cls: "bg-violet-500/15 text-violet-700 dark:text-violet-300" });
              if (!badges.length) return null;
              return (
                <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="dna-preview-detail">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Aktive DNA:</span>
                  {badges.map(b => (
                    <span key={b.k} className={`text-[10px] px-2 py-0.5 rounded-sm font-medium ${b.cls}`}>{b.label}</span>
                  ))}
                </div>
              );
            })()}
          </div>
          <div className="pt-3 border-t border-border">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Social Media</p>
            <p className="text-xs text-muted-foreground mb-3 italic">Links erscheinen im Footer der Website. Instagram (Handle oder URL) wird zusätzlich als echter Feed auf der Demo-Website eingebettet. Ungültige Eingaben werden beim Speichern automatisch verworfen.</p>
            <div className="grid grid-cols-2 gap-4">
              {field("instagram", "Instagram (@handle oder URL)", "text")}
              {field("facebook", "Facebook URL", "text")}
              {field("linkedin", "LinkedIn URL", "text")}
              {field("tiktok", "TikTok URL", "text")}
              {field("whatsapp", "WhatsApp-Nummer (Schnellkontakt auf der Website)", "text")}
            </div>
          </div>
          <div className="pt-3 border-t border-border">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">E-Mail-Absender (Kunden-Website)</p>
            <p className="text-xs text-muted-foreground mb-3 italic">Wenn gesetzt, kommen Buchungs-/Anfrage-Bestätigungen für Endkunden mit diesem Absender (statt <strong>info@signalslt.de</strong>). <strong>Voraussetzung:</strong> Domain muss bei Resend verifiziert sein, sonst Fallback automatisch auf den Standard-Absender. <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-primary underline">Resend-Domain hinzufügen →</a></p>
            <div className="grid grid-cols-2 gap-4">
              {field("email_sender_name", "Absender-Name", "text")}
              {field("email_sender_email", "Absender-E-Mail", "email")}
            </div>
          </div>
          <div className="pt-4 border-t border-border">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Team-Mitglieder</p>
            <p className="text-xs text-muted-foreground mb-3 italic">Portrait, Name, Rolle und Bio pro Mitarbeiter. Sie erscheinen auf der Team-Unterseite (page_type=team) in einem Portrait-Grid.</p>
            <TeamEditor
              companyId={id}
              initialMembers={company.team_members || []}
              onSave={() => load()}
            />
          </div>
        </div>

        {/* Projects */}
        <div className="space-y-3">
          <div className="bg-card border border-border rounded-sm">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Projekte</p>
              <button onClick={openProjectModal} data-testid="new-project-btn" className="p-1 rounded-sm hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                <Plus size={14} />
              </button>
            </div>
            <div className="divide-y divide-border">
              {projects.length === 0 && (
                <div className="px-4 py-6 text-center">
                  <p className="text-xs text-muted-foreground mb-3">Noch kein Projekt angelegt</p>
                  <Button size="sm" variant="outline" onClick={openProjectModal} className="rounded-sm gap-1.5 text-xs">
                    <Plus size={12} /> Projekt erstellen
                  </Button>
                </div>
              )}
              {projects.map(p => {
                const s = PROJECT_STATUS[p.status] || PROJECT_STATUS.geplant;
                const template = templates.find(t => t.id === p.template_id);
                return (
                  <div key={p.id} className="px-4 py-3 space-y-2" data-testid={`project-card-${p.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium text-foreground leading-tight">{p.name}</p>
                      <button onClick={() => handleDeleteProject(p.id)} className="text-muted-foreground hover:text-destructive transition-colors shrink-0">
                        <Trash2 size={12} />
                      </button>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs px-1.5 py-0.5 rounded-sm font-medium ${s.color}`}>{s.label}</span>
                      {template && <span className="text-xs text-muted-foreground">{template.name}</span>}
                    </div>
                    <Link
                      to={`/builder/${p.id}`}
                      data-testid={`open-builder-${p.id}`}
                      className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors"
                    >
                      <Wand2 size={11} /> Zum Builder
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>

          {/* BOOKINGS */}
          <div className="bg-card rounded-sm border border-border overflow-hidden mt-6" data-testid="bookings-section">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <h2 className="text-sm font-bold uppercase tracking-widest">Terminbuchungen</h2>
              <span className="text-xs text-muted-foreground">{bookings.length}</span>
            </div>
            <div className="divide-y divide-border max-h-[420px] overflow-y-auto">
              {bookings.length === 0 && (
                <div className="p-6 text-center text-xs text-muted-foreground italic">Noch keine Buchungen.<br />Aktiviere den Freigabe-Link im Builder, um das Formular auf der Website zu aktivieren.</div>
              )}
              {bookings.map(b => (
                <div key={b.id} className="p-4 space-y-1.5" data-testid={`booking-${b.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-semibold truncate">{b.name}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-sm uppercase tracking-wider ${b.status === "neu" ? "bg-blue-500/15 text-blue-600" : b.status === "bestätigt" ? "bg-green-500/15 text-green-700" : "bg-muted text-muted-foreground"}`}>{b.status}</span>
                    </div>
                    <span className="text-[10px] text-muted-foreground shrink-0">{(b.created_at || "").slice(0, 10)}</span>
                  </div>
                  {b.service && <div className="text-xs text-foreground">{b.service}</div>}
                  {(b.preferred_date || b.preferred_time) && (
                    <div className="text-xs text-muted-foreground">
                      Wunsch: {b.preferred_date || "–"} {b.preferred_time ? `· ${b.preferred_time}` : ""}
                      {b.party_size ? ` · ${b.party_size} Pers.` : ""}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3">
                    {b.email && <a href={`mailto:${b.email}`} className="hover:text-primary">{b.email}</a>}
                    {b.phone && <a href={`tel:${b.phone}`} className="hover:text-primary">{b.phone}</a>}
                  </div>
                  {b.notes && <div className="text-xs text-muted-foreground italic mt-1">„{b.notes}"</div>}
                </div>
              ))}
            </div>
          </div>

          {/* KUNDEN-PORTAL */}
          <div className="bg-card rounded-sm border border-border overflow-hidden mt-6" data-testid="portal-section">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Globe size={14} className="text-cyan-500" />
                <h2 className="text-sm font-bold uppercase tracking-widest">Kunden-Portal</h2>
              </div>
              <Button size="sm" variant="outline" onClick={() => setShowPortalModal(true)} className="rounded-sm gap-1.5 text-xs h-7" data-testid="create-portal-btn">
                <Plus size={12} /> Zugang erstellen
              </Button>
            </div>
            <div className="divide-y divide-border">
              {portalAccounts.length === 0 && (
                <div className="px-4 py-6 text-center">
                  <p className="text-xs text-muted-foreground mb-3">Noch kein Kunden-Portal-Zugang</p>
                  <Button size="sm" variant="outline" onClick={() => setShowPortalModal(true)} className="rounded-sm gap-1.5 text-xs">
                    <Users2 size={12} /> Zugang erstellen
                  </Button>
                </div>
              )}
              {portalAccounts.map(acc => (
                <div key={acc.id} className="px-4 py-3 space-y-2" data-testid={`portal-account-${acc.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium">{acc.contact_name || acc.email}</p>
                      <p className="text-xs text-muted-foreground">{acc.email} · {acc.plan_name}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-medium ${acc.is_active ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" : "bg-muted text-muted-foreground"}`}>
                        {acc.is_active ? "Aktiv" : "Inaktiv"}
                      </span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${acc.subscription_status === "aktiv" ? "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400" : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-600"}`}>
                        {acc.subscription_status}
                      </span>
                    </div>
                  </div>
                  {acc.portal_url && acc.is_active && (
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="flex-1 min-w-0 bg-muted/50 rounded px-2 py-1 text-[10px] font-mono text-muted-foreground truncate">{acc.portal_url}</div>
                      <button
                        onClick={() => handleCopyLink(acc.portal_url, acc.id)}
                        title="Link kopieren"
                        className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                        data-testid={`copy-portal-link-${acc.id}`}
                      >
                        {copiedToken === acc.id ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
                      </button>
                      <a href={acc.portal_url} target="_blank" rel="noreferrer" className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="Portal öffnen">
                        <ExternalLink size={12} />
                      </a>
                      <button
                        onClick={() => handleDeactivatePortal(acc.id)}
                        title="Zugang deaktivieren"
                        className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-destructive transition-colors"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* New Project Modal */}
      <Dialog open={showProjectModal} onOpenChange={setShowProjectModal}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Neues Projekt</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateProject} className="space-y-4" data-testid="create-project-form">
            {detectedProfile && prefillApplied && (
              <div className="rounded-sm border border-primary/30 bg-primary/5 px-3 py-2.5 flex items-start gap-2" data-testid="project-prefill-banner">
                <Sparkles size={14} className="text-primary shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-primary leading-tight">
                    Branche „{detectedProfile.label}" erkannt — vorausgefüllt
                  </p>
                  <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                    Empfohlenes Template
                    {detectedProfile.recommended_template ? (
                      <span className="font-medium text-foreground"> · {(templates.find(t => t.id === detectedProfile.recommended_template) || {}).name || detectedProfile.recommended_template}</span>
                    ) : ""}
                    {" · "}{(detectedProfile.suggested_pages || []).length} Unterseiten-Vorschläge.
                    Alles änderbar.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={resetPrefill}
                  className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1 shrink-0"
                  data-testid="project-prefill-reset-btn"
                >
                  <RotateCcw size={11} /> Zurücksetzen
                </button>
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Projektname *</Label>
              <Input value={projectForm.name} onChange={e => setProjectForm(f => ({ ...f, name: e.target.value }))} required placeholder="z.B. Website Relaunch 2025" className="rounded-sm" data-testid="project-name-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Template *</Label>
              <Select value={projectForm.template_id} onValueChange={v => setProjectForm(f => ({ ...f, template_id: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="template-select">
                  <SelectValue placeholder="Template wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Notizen</Label>
              <Textarea value={projectForm.notes} onChange={e => setProjectForm(f => ({ ...f, notes: e.target.value }))} className="rounded-sm" rows={2} />
            </div>
            <div className="pt-3 border-t border-border">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Unterseiten <span className="text-muted-foreground/60 normal-case">(optional)</span></Label>
                {requestedSubpages.length > 0 && (
                  <span className="text-[10px] text-primary">{requestedSubpages.length} geplant</span>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground mb-3 italic">Werden nach Klick auf „Website erstellen" im Builder automatisch mit-generiert. Kann später frei ergänzt werden.</p>
              <SubpagesEditor
                subpages={requestedSubpages}
                setSubpages={setRequestedSubpages}
                compact
              />
            </div>
            <div className="flex gap-2 justify-end sticky bottom-0 bg-background pt-3 border-t border-border -mx-6 px-6">
              <Button type="button" variant="outline" onClick={() => { setShowProjectModal(false); setRequestedSubpages([]); setPrefillApplied(false); }} className="rounded-sm">Abbrechen</Button>
              <Button type="submit" disabled={saving} className="rounded-sm" data-testid="create-project-submit-btn">{saving ? "..." : "Erstellen"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Portal Access Modal */}
      <Dialog open={showPortalModal} onOpenChange={setShowPortalModal}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold flex items-center gap-2">
              <Globe size={16} className="text-cyan-500" /> Kunden-Portal-Zugang erstellen
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePortalAccess} className="space-y-4" data-testid="create-portal-form">
            <div className="space-y-1">
              <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Kunden-E-Mail *</Label>
              <Input
                type="email"
                value={portalForm.email}
                onChange={e => setPortalForm(f => ({ ...f, email: e.target.value }))}
                required
                placeholder="kunde@firma.de"
                className="rounded-sm"
                data-testid="portal-email-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Ansprechpartner (optional)</Label>
              <Input
                value={portalForm.contact_name}
                onChange={e => setPortalForm(f => ({ ...f, contact_name: e.target.value }))}
                placeholder="Max Mustermann"
                className="rounded-sm"
                data-testid="portal-contact-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Abo-Paket *</Label>
              <Select value={portalForm.plan} onValueChange={v => setPortalForm(f => ({ ...f, plan: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="portal-plan-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter — 39,99€/Monat</SelectItem>
                  <SelectItem value="business">Business — 59,99€/Monat</SelectItem>
                  <SelectItem value="premium">Premium — 99€/Monat</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground bg-muted/30 rounded p-3">
              Ein persönlicher Zugangslink wird erstellt. Kopieren Sie ihn und senden Sie ihn an den Kunden.
            </p>
            <div className="flex gap-2 justify-end pt-2 border-t border-border">
              <Button type="button" variant="outline" onClick={() => setShowPortalModal(false)} className="rounded-sm">Abbrechen</Button>
              <Button type="submit" disabled={creatingPortal} className="rounded-sm gap-1.5" data-testid="portal-create-submit-btn">
                {creatingPortal ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                {creatingPortal ? "Erstelle..." : "Zugang erstellen"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
